import {
  CompanyPermissions,
  TeamAndDetails,
  TeamRoleAndDetails,
} from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/company';
import { TheraManagerExtraPermissions, TheraTeamRoles, TheraTeamRolesDesc } from 'lib/constants';
import { logError } from './error';
import { ICompanyAffiliation } from 'types';

export const getRoleLabel = (role: string) => {
  return TheraTeamRolesDesc[role]?.name;
};

// Shown on the disabled approve/reject/reopen controls so a manager who can view a timesheet but
// isn't an approver for that employee's team understands why the action is unavailable.
export const TIMESHEET_APPROVE_DENIED_TOOLTIP =
  "You can view this timesheet, but approving requires payroll-approval permission for this employee's team.";

export const getManagerRolesList = (companyPermissions: CompanyPermissions): string[] => {
  if (!companyPermissions?.teams) return [];
  const teamCount = Object.keys(companyPermissions.teams)?.length;
  if (teamCount === 0) return [];
  const list = [];
  for (const teamId in companyPermissions.teams) {
    const team = companyPermissions.teams[teamId];
    if (!team) {
      const error = new Error(`team not found for id [${teamId}]`);
      logError(error);
      continue;
    }
    const roleName = TheraTeamRolesDesc[team.role]?.name;
    list.push(roleName);
  }
  // return unique values
  return [...new Set(list)];
};

export const getManagerTeamsList = (
  companyPermissions: CompanyPermissions,
  teams: Record<string, TeamAndDetails>,
): string[] => {
  if (!companyPermissions?.teams) return [];
  const teamCount = Object.keys(companyPermissions.teams)?.length;
  if (teamCount === 0) return [];
  const list = [];
  for (const teamId in companyPermissions.teams) {
    const team = teams[teamId];
    if (!team) {
      const error = new Error(`team not found for id [${teamId}]`);
      logError(error);
      continue;
    }
    list.push(team.name);
  }
  return [...new Set(list)];
};

export const getTeamByContractId = (contractId: string, teams: Record<string, TeamAndDetails>) => {
  const teams_ = Object.entries(teams).map(([teamId, team]) => ({
    id: teamId,
    name: team.name,
    contractIds: team.contractIds,
  }));
  const team = teams_.find((team) => team.contractIds.includes(contractId));
  return {
    id: team?.id,
    name: team?.name,
  };
};

export const checkIfUserHasAccessToCompanyAndRole = (
  companyId: string,
  role: 'manager' | 'worker',
  affiliationsByCompany: Record<string, ICompanyAffiliation>,
) => {
  if (!companyId || !role || !affiliationsByCompany) return false;
  const saved_company = affiliationsByCompany[companyId];
  if (!saved_company) return false;
  const hasAccess =
    (role === 'manager' && saved_company?.orgRoleAndDetails?.some((roleDetail) => roleDetail.role === 'role_admin')) ||
    (role === 'manager' &&
      saved_company?.orgRoleAndDetails?.some((roleDetail) => roleDetail.role === 'role_manager')) ||
    (role === 'manager' &&
      saved_company?.orgRoleAndDetails?.some((roleDetail) => roleDetail.role === 'role_org_manager')) ||
    (role === 'manager' &&
      saved_company?.orgRoleAndDetails?.some((roleDetail) => roleDetail.role === 'role_apar_manager')) ||
    (role === 'manager' && saved_company?.teams && Object.keys(saved_company?.teams).length > 0) ||
    (role === 'worker' && saved_company?.contracts?.length > 0);
  return hasAccess;
};

/**@deprecated */
// any manager can move contractors between teams if they're part of both the teams
export const canMoveContractorsAmongTeams = ({
  affiliationsByCompany,
  currentCompanyId,
  currentContractTeamId,
}: {
  affiliationsByCompany: Record<string, ICompanyAffiliation>;
  currentCompanyId: string;
  currentContractTeamId: string;
}) => {
  // return false if teams are not loaded
  if (!affiliationsByCompany) return false;
  // check if they have permissions on more than one team
  const teams = affiliationsByCompany?.[currentCompanyId]?.teams;
  if (!teams || !Object.keys(teams)?.length) return false;

  const isManagerOfManyTeams = (teamsObj: Record<string, TeamRoleAndDetails>, targetTeamId: string) => {
    const teamIds = Object.keys(teamsObj);
    const numberOfTeams = teamIds.length;

    if (numberOfTeams > 1 && teamIds.includes(targetTeamId)) {
      return true;
    }

    return false;
  };
  const result = isManagerOfManyTeams(teams, currentContractTeamId);

  if (result) {
    return true;
  } else {
    return false;
  }
};

export type TeamRolePermission = {
  id: string;
  name: string;
  description: string;
  granted: boolean;
};

export type TeamRoleBreakdown = {
  teamId: string;
  teamName: string;
  role: string;
  roleName: string;
  roleDescription: string;
  supportsExtraPermissions: boolean;
  extraPermissions: TeamRolePermission[];
};

const ROLES_WITH_EXTRA_PERMISSIONS: string[] = [
  TheraTeamRoles.role_people_manager,
  TheraTeamRoles.role_people_manager_and_payer,
];

export const getManagerTeamRoleBreakdown = (
  companyPermissions: CompanyPermissions,
  teams: Record<string, TeamAndDetails>,
): TeamRoleBreakdown[] => {
  if (!companyPermissions?.teams) return [];
  const breakdown: TeamRoleBreakdown[] = [];
  for (const teamId in companyPermissions.teams) {
    const assignment = companyPermissions.teams[teamId];
    if (!assignment) continue;
    const granted = assignment.permissions ?? [];
    const supportsExtraPermissions = ROLES_WITH_EXTRA_PERMISSIONS.includes(assignment.role);
    breakdown.push({
      teamId,
      teamName: teams?.[teamId]?.name ?? 'Unknown team',
      role: assignment.role,
      roleName: TheraTeamRolesDesc[assignment.role]?.name ?? assignment.role,
      roleDescription: TheraTeamRolesDesc[assignment.role]?.description ?? '',
      supportsExtraPermissions,
      extraPermissions: supportsExtraPermissions
        ? Object.values(TheraManagerExtraPermissions).map((permission) => ({
            id: permission.id,
            name: permission.name,
            description: permission.description,
            granted: granted.includes(permission.id as never),
          }))
        : [],
    });
  }
  return breakdown.sort((a, b) => a.teamName.localeCompare(b.teamName));
};
